import * as Sentry from '@sentry/vue'
import { fetchCanvasBlocksFromUrl } from '@/utils/CanvasBlocksLoader.utils'

/**
 * Processes slides to fetch canvas blocks for those that have canvasBlocksUrl
 * @param {Array} slides - Array of slide objects
 * @returns {Promise<Array>} - Array of slides with canvas blocks fetched
 */
export const processCanvasBlocksForSlides = async (slides) => {
  if (!slides?.length) return slides

  const processedSlides = await Promise.allSettled(
    slides.map(async (slide) => {
      // Always return the slide with updated content from API
      // Only fetch canvas blocks if canvasBlocksUrl exists
      if (!slide.canvasBlocksUrl) {
        return slide // Return the slide as-is from API (with updated content)
      }

      try {
        const canvasBlocks = await fetchCanvasBlocksFromUrl(slide.canvasBlocksUrl)
        return {
          ...slide,
          canvasBlocks: canvasBlocks || slide.canvasBlocks,
        }
      } catch (error) {
        Sentry.captureException(error)
        return slide // Return the slide as-is from API if canvas blocks fetch fails
      }
    }),
  )

  return processedSlides.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value
    }
    // If failed, return original slide from API
    return slides[index]
  })
}

/**
 * Finds a slide by its ID in the given slides array
 * @param {Array} slides - Array of slide objects
 * @param {number} slideId - ID of the slide to find
 * @returns {Object|null} The found slide or null if not found
 */
export const findSlideById = (slides, slideId) => {
  if (!slides || !Array.isArray(slides)) return null
  return slides.find((slide) => slide.id === slideId) || null
}

/**
 * Finds the original index of a slide in the original slides array
 * @param {Array} originalSlides - Original slides array
 * @param {number} slideId - ID of the slide to find
 * @returns {number} Index of the slide, or -1 if not found
 */
export const findSlideOriginalIndex = (originalSlides, slideId) => {
  return originalSlides.findIndex((slide) => slide.id === slideId)
}

/**
 * Finds the closest available slide when the target slide was deleted
 * @param {Array} updatedSlides - Updated slides array
 * @param {number} originalIndex - Original index of the deleted slide
 * @returns {Object|null} The closest available slide or null
 */
export const findClosestAvailableSlide = (updatedSlides, originalIndex) => {
  // Try to find a slide at a smaller index (moving backwards from the original position)
  for (let i = originalIndex - 1; i >= 0; i--) {
    if (i < updatedSlides.length) {
      return updatedSlides[i]
    }
  }

  // If no smaller index exists, return the first slide if available
  return updatedSlides.length > 0 ? updatedSlides[0] : null
}

/**
 * Handles the case when a slide with annotation ID was deleted
 * @param {Array} originalSlides - Slides before the update
 * @param {Array} updatedSlides - Slides after the update
 * @param {number} slideId - ID of the deleted slide
 * @returns {Object|null} The closest available slide or null
 */
export const handleDeletedAnnotatedSlide = (originalSlides, updatedSlides, slideId) => {
  const originalIndex = findSlideOriginalIndex(originalSlides, slideId)

  if (originalIndex === -1) {
    return null
  }

  return findClosestAvailableSlide(updatedSlides, originalIndex)
}

/**
 * Processes slide selection based on annotation ID
 * @param {Array} originalSlides - Slides before the update
 * @param {Array} updatedSlides - Slides after the update
 * @param {string} firstSlideIdWithAnnotation - ID of the first slide with annotation
 * @returns {Object|null} The slide to activate or null
 */
export const processAnnotatedSlideSelection = (originalSlides, updatedSlides, firstSlideIdWithAnnotation) => {
  const slideId = Number(firstSlideIdWithAnnotation)
  const targetSlide = findSlideById(updatedSlides, slideId)

  // If the slide with the annotation ID exists, return it
  if (targetSlide) {
    return targetSlide
  }

  // If the slide doesn't exist (was deleted), find the closest available slide
  return handleDeletedAnnotatedSlide(originalSlides, updatedSlides, slideId)
}

/**
 * Determines which slide should be set as active after presentation update
 * @param {Array} originalSlides - Slides before the update
 * @param {Array} updatedSlides - Slides after the update
 * @param {string|undefined} firstSlideIdWithAnnotation - ID of the first slide with annotation
 * @returns {Object|null} The slide to set as active, or null if no slide should be set
 */
export const determineActiveSlide = (
  originalSlides,
  updatedSlides,
  firstSlideIdWithAnnotation,
) => {
  // If there's a slide with annotation, process it for activation
  if (firstSlideIdWithAnnotation) {
    return processAnnotatedSlideSelection(originalSlides, updatedSlides, firstSlideIdWithAnnotation)
  }

  // No slide should be set as active
  return null
}

/**
 * Preserves annotation properties from original slides to new slides
 * @param {Array} processedSlides - Slides with canvas blocks processed
 * @param {Array} originalSlides - Original slides before update to preserve annotation properties
 * @returns {Array} Slides with preserved annotation properties
 */
export const preserveAnnotationProperties = (processedSlides, originalSlides = []) => {
  if (!processedSlides || !Array.isArray(processedSlides)) return []
  if (!originalSlides || !Array.isArray(originalSlides)) return processedSlides

  return processedSlides.map((newSlide) => {
    const originalSlide = originalSlides.find((slide) => slide.id === newSlide.id)
    if (originalSlide) {
      return {
        ...newSlide,
        annotationType: originalSlide.annotationType,
        annotationSource: originalSlide.annotationSource,
        annotationIsVisible: originalSlide.annotationIsVisible,
      }
    }
    return newSlide
  })
}

/**
 * Validates that the presentation ID exists
 * @param {string} presentationId - The presentation ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validatePresentationId = (presentationId) => {
  return !!presentationId
}

/**
 * Validates if an annotation object contains meaningful data
 * @param {*} annotation - The annotation to validate
 * @returns {boolean} True if annotation has meaningful data, false otherwise
 */
export const isValidAnnotation = (annotation) => {
  if (!annotation || typeof annotation !== 'object') {
    return false
  }

  // Check if it's an empty object
  if (Object.keys(annotation).length === 0) {
    return false
  }

  // Check if all values are falsy (null, undefined, empty string, etc.)
  return Object.values(annotation).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0
    }
    if (typeof value === 'object' && value !== null) {
      return Object.keys(value).length > 0
    }
    return Boolean(value)
  })
}

/**
 * Identifies newly created slides by comparing original and processed slide arrays
 * @param {Array} originalSlides - Original slides before update
 * @param {Array} processedSlides - Processed slides after API fetch
 * @returns {Array} Array of newly created slides
 */
export const identifyNewSlides = (originalSlides = [], processedSlides = []) => {
  // Handle null/undefined inputs
  if (!processedSlides || !Array.isArray(processedSlides)) {
    return []
  }

  if (!originalSlides || !Array.isArray(originalSlides) || originalSlides.length === 0) {
    return processedSlides // All slides are new if no original slides exist
  }

  const originalSlideIds = new Set(originalSlides.map((slide) => slide.id))
  return processedSlides.filter((slide) => !originalSlideIds.has(slide.id))
}

/**
 * Finds the first slide with annotation based on presentation slide order
 * @param {Array} slides - Array of presentation slides
 * @param {Object} annotationsBySlide - Annotations mapped by slide ID
 * @returns {string|null} First slide ID with annotation, or null
 */
export const findFirstSlideWithAnnotation = (slides, annotationsBySlide) => {
  if (!slides || !Array.isArray(slides) || !annotationsBySlide) {
    return null
  }

  const slideWithAnnotation = slides.find((slide) => {
    const annotation = annotationsBySlide[slide.id]
    return isValidAnnotation(annotation)
  })
  return slideWithAnnotation?.id?.toString() || null
}
